import React from 'react';
import './ErrorBoundary.css';
import { trackEvent } from '../analytics';

/**
 * React error boundary that catches render-phase exceptions in its children
 * and shows a friendly fallback UI instead of a white screen.
 *
 * Event handlers, async callbacks, and server-side errors are NOT caught by
 * error boundaries — those must be handled with try/catch + user-facing state
 * in the originating component (see usage in PurchasePage, Studio, etc.).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for dev; in production this is where we'd forward to
    // an error-reporting service (Sentry, etc.) if/when one is added.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    const description = ((error && error.message) || String(error)).slice(0, 100);
    trackEvent('exception', { description, fatal: false });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback, scope } = this.props;
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback({ error: this.state.error, reset: this.handleReset })
          : fallback;
      }

      return (
        <div className="error-boundary" role="alert" data-testid="error-boundary">
          <div className="error-boundary-card">
            <h2>Something went wrong</h2>
            <p>
              {scope
                ? `We hit an unexpected error while loading ${scope}.`
                : 'We hit an unexpected error.'}{' '}
              You can try again or reload the page. If the problem continues,
              please contact support.
            </p>
            <div className="error-boundary-actions">
              <button
                type="button"
                className="btn-teal"
                onClick={this.handleReset}
              >
                Try again
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={this.handleReload}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
